/**
 * Top-down yard plan drawn on the sign-in pages: rows A–F, 20′ and 40′ containers to scale, shaded by stack height.
 * Pure layout data; the component only renders it.
 */
export interface YardBlock {
  x: number;
  y: number;
  w: number;
  tier: 1 | 2 | 3;
  /** The container that drives into its slot once when the page loads. */
  arriving?: boolean;
}

export interface YardLayout {
  width: number;
  height: number;
  blocks: YardBlock[];
  rows: { label: string; y: number }[];
  bays: { label: string; x: number }[];
}

// One token per slot: 20′ = "1", 40′ = "2"; the digit after it is the stack height; "_" is an empty 20′ slot,
// "*" the empty 40′ slot the arriving container fills.
const ROWS: string[] = [
  "21 13 12 _ 22 11 23 _ 12 21 13",
  "23 22 11 12 _ 21 13 22 11 _ 12",
  "12 21 * 13 22 _ 11 21 12 23",
  "11 23 12 22 _ 13 21 11 22 12",
  "22 _ 13 21 12 11 23 _ 21 13 11",
  "13 12 21 _ 22 13 11 12 21 22"
];

const UNIT = 30;       // a 20′ container, top-down
const GAP = 3;         // between containers in a row
const HEIGHT = 12;
const ROW_GAP = 9;
const LANE_GAP = 22;   // truck lane after row C
const LEFT = 22;       // room for row letters
const TOP = 18;        // room for bay numbers

export function buildYard(): YardLayout {
  const blocks: YardBlock[] = [];
  const rows: { label: string; y: number }[] = [];
  let y = TOP;
  let maxUnits = 0;
  ROWS.forEach((row, r) => {
    let unit = 0;
    for (const token of row.split(" ")) {
      const x = LEFT + unit * (UNIT + GAP);
      if (token === "_") {
        unit += 1;
        continue;
      }
      if (token === "*") {
        blocks.push({ x, y, w: UNIT * 2 + GAP, tier: 3, arriving: true });
        unit += 2;
        continue;
      }
      const span = Number(token[0]);
      blocks.push({ x, y, w: UNIT * span + GAP * (span - 1), tier: Number(token[1]) as 1 | 2 | 3 });
      unit += span;
    }
    maxUnits = Math.max(maxUnits, unit);
    rows.push({ label: String.fromCharCode(65 + r), y: y + HEIGHT / 2 });
    y += HEIGHT + (r === 2 ? LANE_GAP : ROW_GAP);
  });
  // yards number 20′ bays odd: 01, 03, 05 …
  const bays = Array.from({ length: Math.ceil(maxUnits / 2) }, (_, i) => ({
    label: String(i * 4 + 1).padStart(2, "0"),
    x: LEFT + i * 2 * (UNIT + GAP) + UNIT / 2
  }));
  return { width: LEFT + maxUnits * (UNIT + GAP), height: y - ROW_GAP, blocks, rows, bays };
}

export const YARD_BLOCK_HEIGHT = HEIGHT;
